import mermaid from 'mermaid';

/**
 * Very small “last-resort” validator for the two chart flavours
 * defined in the prompt.  Pass ONLY the text that sits inside
 * the ```mermaid fences.
 *
 * @param {string} code  Raw Mermaid chart text.
 * @returns {{ valid: boolean, errors: string[] }}
 */
/**
 * Quick validator for the “pie” and “xychart-beta” Mermaid
 * grammars defined in your prompt.
 *
 * Pass ONLY the text inside the ```mermaid fences.
 *
 * @param {string} code
 * @returns {{ valid: boolean, errors: string[] }}
 */
function validateMermaid(code) {
  const errors = [];
  const lines = code.split(/\r?\n/).map((l) => l.replace(/\s+$/, '')); // trim RHS spaces

  if (!lines.length || !lines[0].trim()) {
    return { valid: false, errors: ['Chart block is empty'] };
  }

  const firstLine = lines[0].trim();

  /* ---------- PIE CHART ---------- */
  if (firstLine.startsWith('pie')) {
    // Header must be: pie <spaces> title <spaces> "Some Title"  OR  pie <spaces> title <spaces> Some Title
    if (!/^pie\s+title\s+(?:"[^"]+"|.+)$/.test(firstLine)) {
      errors.push('Pie header must be: pie title "Your Title"  (quotes optional)');
    }

    for (let i = 1; i < lines.length; i++) {
      const row = lines[i].trim();
      if (!row) continue; // allow blank lines
      if (!/^"[^"]+"\s*:\s*\d+$/.test(row)) {
        errors.push(`Invalid slice row → ${row}`);
      }
    }

    /* ---------- XY CHART ---------- */
  } else if (firstLine === 'xychart-beta') {
    const seen = { title: false, x: false, y: false, series: false };
    let xLen = 0,
      dataLen = 0;

    for (let i = 1; i < lines.length; i++) {
      const row = lines[i].trim();
      if (!row) continue; // skip blank lines

      if (row.startsWith('title ')) {
        seen.title = true;
        if (!/^title\s+"[^"]+"$/.test(row)) errors.push('Bad title line');
      } else if (row.startsWith('x-axis ')) {
        seen.x = true;
        const m = row.match(/^x-axis\s+\[([^\]]+)\]$/);
        if (!m) errors.push('Bad x-axis line');
        else xLen = m[1].split(',').length;
      } else if (row.startsWith('y-axis ')) {
        seen.y = true;
        if (!/^y-axis\s+"[^"]+"\s+-?\d+\s+-->\s+-?\d+$/.test(row)) errors.push('Bad y-axis line');
      } else if (row.startsWith('bar ') || row.startsWith('line ')) {
        seen.series = true;
        const m = row.match(/^(bar|line)\s+\[([^\]]+)\]$/);
        if (!m) errors.push('Bad series line');
        else dataLen = m[2].split(',').length;
      } else {
        errors.push(`Unrecognised line → ${row}`);
      }
    }

    // Mandatory elements present?
    for (const k of ['title', 'x', 'y', 'series']) {
      if (!seen[k]) errors.push(`Missing ${k} line`);
    }
    // x-axis / data length match?
    if (seen.x && seen.series && xLen !== dataLen) {
      errors.push(`x-axis labels (${xLen}) ≠ data points (${dataLen})`);
    }

    /* ---------- UNKNOWN CHART ---------- */
  } else {
    errors.push('First line must start with "pie" or equal "xychart-beta"');
  }

  return { valid: errors.length === 0, errors };
}

const mermaidChart = (code) => {
  const validateRes = validateMermaid(code);
  if (validateRes.valid) {
    return `<div class="mermaid">${code}</div>`;
  } else {
    console.error(`Mermaid validation errors:`, validateRes.errors);
    return `<pre>${code}</pre>`;
  }
};

const MermaidPlugin = (md) => {
  md.mermaid = mermaid;

  /* may want to reconsider later
  mermaid.loadPreferences = (preferenceStore) => {
    let mermaidTheme = preferenceStore.get('mermaid-theme');
    if (mermaidTheme === undefined) {
      mermaidTheme = 'default';
    }
    let ganttAxisFormat = preferenceStore.get('gantt-axis-format');
    if (ganttAxisFormat === undefined) {
      ganttAxisFormat = '%Y-%m-%d';
    }
    mermaid.initialize({
      startOnLoad: false,
      theme: mermaidTheme,
      gantt: {
        axisFormatter: [
          [
            ganttAxisFormat,
            (d) => {
              return d.getDay() === 1;
            },
          ],
        ],
      },
    });
    return {
      'mermaid-theme': mermaidTheme,
      'gantt-axis-format': ganttAxisFormat,
    };
  };
  */

  const temp = md.renderer.rules.fence.bind(md.renderer.rules);
  md.renderer.rules.fence = (tokens, idx, options, env, slf) => {
    const token = tokens[idx];
    const code = token.content.trim();
    if (token.info === 'mermaid') {
      let res = '';
      try {
        res = mermaidChart(code);
      } catch (e) {
        console.error(`Mermaid error`, e);
        res = '';
      }
      return res;
    }
    const firstLine = code.split(/\n/)[0].trim();
    if (firstLine === 'gantt' || firstLine === 'sequenceDiagram' || firstLine.match(/^graph (?:TB|BT|RL|LR|TD);?$/)) {
      return mermaidChart(code);
    }
    return temp(tokens, idx, options, env, slf);
  };
};

export default MermaidPlugin;
